import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ApiService } from "../api/api.service";
import {
  ForumAttachment,
  ForumAttachmentConfig,
  ForumAttachmentCreatePayload,
  ForumAttachmentS3UrlResponse,
  ForumMember,
  ForumOverviewGroup,
  ForumPost,
  ForumPostCreatePayload,
  ForumPostReaction,
  ForumPostUpdatePayload,
  ForumReactionActionPayload,
  ForumReactionOption,
  ForumThreadReactionDetails,
  ForumThreadReaction,
  ForumThreadRead,
  ForumThread,
  ForumThreadCreatePayload,
  ForumThreadUpdatePayload,
} from "src/app/shared/interfaces/forum";
import { PaginationData } from "src/app/shared/interfaces/orgadmin/course-organization";

@Injectable({
  providedIn: "root",
})
export class ForumService {
  constructor(private readonly apiService: ApiService) {}

  getThreads(
    courseId: string,
    groupId: string,
    search?: string,
    archived?: boolean,
    page?: number,
  ): Observable<PaginationData<ForumThread>> {
    return this.apiService.getRequest<PaginationData<ForumThread>>(
      `courses/${courseId}/groups/${groupId}/forum/threads/`,
      {
        params: {
          ...(search ? { search } : {}),
          ...(typeof archived === "boolean" ? { archived } : {}),
          ...(page ? { page } : {}),
        },
      },
    );
  }

  getMembers(courseId: string, groupId: string): Observable<ForumMember[]> {
    return this.apiService.getRequest<ForumMember[]>(
      `courses/${courseId}/groups/${groupId}/forum/members/`,
    );
  }

  getAttachmentS3Url(
    courseId: string,
    groupId: string,
    filename: string,
  ): Observable<ForumAttachmentS3UrlResponse> {
    return this.apiService.postRequest<ForumAttachmentS3UrlResponse>(
      `courses/${courseId}/groups/${groupId}/forum/attachments/s3_url/`,
      {
        filename,
      },
    );
  }

  getAttachmentConfig(
    courseId: string,
    groupId: string,
  ): Observable<ForumAttachmentConfig> {
    return this.apiService.getRequest<ForumAttachmentConfig>(
      `courses/${courseId}/groups/${groupId}/forum/attachments/config/`,
    );
  }

  getOverviewGroups(
    page?: number,
    groupName?: string,
    courseName?: string,
    ordering?: "group_name" | "course_name",
  ): Observable<PaginationData<ForumOverviewGroup>> {
    return this.apiService.getRequest<PaginationData<ForumOverviewGroup>>(
      "forum/groups/",
      {
        params: {
          ...(typeof page === "number" ? { page } : {}),
          ...(groupName ? { group_name: groupName } : {}),
          ...(courseName ? { course_name: courseName } : {}),
          ...(ordering ? { ordering } : {}),
        },
      },
    );
  }

  getThreadDetails(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<ForumThread> {
    return this.apiService.getRequest<ForumThread>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/`,
    );
  }

  getThreadEditHistory(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<ForumThread[]> {
    return this.apiService.getRequest<ForumThread[]>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/edit-history/`,
    );
  }

  createThread(
    courseId: string,
    groupId: string,
    payload: ForumThreadCreatePayload,
  ): Observable<ForumThread> {
    return this.apiService.postRequest<ForumThread>(
      `courses/${courseId}/groups/${groupId}/forum/threads/`,
      payload,
    );
  }

  updateThread(
    courseId: string,
    groupId: string,
    threadId: string,
    payload: ForumThreadUpdatePayload,
  ): Observable<ForumThread> {
    return this.apiService.patchRequest<ForumThread>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/`,
      payload,
    );
  }

  deleteThread(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/`,
    );
  }

  deletePernamentThread(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/permanent/`,
    );
  }

  restoreThread(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<ForumThread> {
    return this.apiService.postRequest<ForumThread>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/restore/`,
      {},
    );
  }

  pinThread(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<ForumThread> {
    return this.apiService.postRequest<ForumThread>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/pin/`,
      {},
    );
  }

  unpinThread(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/pin/`,
    );
  }

  getThreadPosts(
    courseId: string,
    groupId: string,
    threadId: string,
    ordering?: "created_at" | "-created_at",
    page?: number,
  ): Observable<PaginationData<ForumPost>> {
    return this.apiService.getRequest<PaginationData<ForumPost>>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/`,
      {
        params: {
          ...(ordering ? { ordering } : {}),
          ...(page ? { page } : {}),
        },
      },
    );
  }

  createPost(
    courseId: string,
    groupId: string,
    threadId: string,
    payload: ForumPostCreatePayload,
  ): Observable<ForumPost> {
    return this.apiService.postRequest<ForumPost>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/`,
      payload,
    );
  }

  updatePost(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
    payload: ForumPostUpdatePayload,
  ): Observable<ForumPost> {
    return this.apiService.patchRequest<ForumPost>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/${postId}/`,
      payload,
    );
  }

  getPostEditHistory(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
  ): Observable<ForumPost[]> {
    return this.apiService.getRequest<ForumPost[]>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/${postId}/edit-history/`,
    );
  }

  deletePost(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/${postId}/`,
    );
  }

  getPostAttachments(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
  ): Observable<PaginationData<ForumAttachment>> {
    return this.apiService.getRequest<PaginationData<ForumAttachment>>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/${postId}/attachments/`,
    );
  }

  createPostAttachment(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
    payload: ForumAttachmentCreatePayload,
  ): Observable<ForumAttachment> {
    return this.apiService.postRequest<ForumAttachment>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/${postId}/attachments/`,
      payload,
    );
  }

  deletePostAttachment(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
    attachmentId: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/${postId}/attachments/${attachmentId}/`,
    );
  }

  getThreadAttachments(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<PaginationData<ForumAttachment>> {
    return this.apiService.getRequest<PaginationData<ForumAttachment>>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/attachments/`,
    );
  }

  createThreadAttachment(
    courseId: string,
    groupId: string,
    threadId: string,
    payload: ForumAttachmentCreatePayload,
  ): Observable<ForumAttachment> {
    return this.apiService.postRequest<ForumAttachment>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/attachments/`,
      payload,
    );
  }

  deleteThreadAttachment(
    courseId: string,
    groupId: string,
    threadId: string,
    attachmentId: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/attachments/${attachmentId}/`,
    );
  }

  getThreadReads(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<ForumThreadRead[]> {
    return this.apiService.getRequest<ForumThreadRead[]>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/reads/`,
    );
  }

  favoriteThread(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<void> {
    return this.apiService.postRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/favorite/`,
      {},
    );
  }

  unfavoriteThread(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/favorite/`,
    );
  }

  lockAllThreads(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<void> {
    return this.apiService.postRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/lock-all/`,
      {},
    );
  }

  unlockAllThreads(
    courseId: string,
    groupId: string,
    threadId: string,
  ): Observable<void> {
    return this.apiService.postRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/unlock-all/`,
      {},
    );
  }

  getReactionOptions(
    courseId: string,
    groupId: string,
  ): Observable<ForumReactionOption[]> {
    return this.apiService.getRequest<ForumReactionOption[]>(
      `courses/${courseId}/groups/${groupId}/forum/reactions/options/`,
    );
  }

  createThreadReaction(
    courseId: string,
    groupId: string,
    threadId: string,
    payload: ForumReactionActionPayload,
  ): Observable<ForumThreadReaction> {
    return this.apiService.postRequest<ForumThreadReaction>(
      `courses/${courseId}/groups/${groupId}/forum/reactions/threads/${threadId}/`,
      payload,
    );
  }

  deleteThreadReaction(
    courseId: string,
    groupId: string,
    threadId: string,
    emoji: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/reactions/threads/${threadId}/?emoji=${encodeURIComponent(
        emoji,
      )}`,
    );
  }

  createPostReaction(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
    payload: ForumReactionActionPayload,
  ): Observable<ForumPostReaction> {
    return this.apiService.postRequest<ForumPostReaction>(
      `courses/${courseId}/groups/${groupId}/forum/reactions/threads/${threadId}/posts/${postId}/`,
      payload,
    );
  }

  deletePostReaction(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
    emoji: string,
  ): Observable<void> {
    return this.apiService.deleteRequest<void>(
      `courses/${courseId}/groups/${groupId}/forum/reactions/threads/${threadId}/posts/${postId}/?emoji=${encodeURIComponent(
        emoji,
      )}`,
    );
  }

  getThreadReactionDetails(
    courseId: string,
    groupId: string,
    threadId: string,
    emoji: string,
  ): Observable<ForumThreadReactionDetails> {
    return this.apiService.getRequest<ForumThreadReactionDetails>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/reactions/?emoji=${encodeURIComponent(
        emoji,
      )}`,
    );
  }

  getPostReactions(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
  ): Observable<ForumPostReaction[]> {
    return this.apiService.getRequest<ForumPostReaction[]>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/${postId}/reactions/`,
    );
  }

  getPostReactionDetails(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
    emoji: string,
  ): Observable<ForumThreadReactionDetails> {
    return this.apiService.getRequest<ForumThreadReactionDetails>(
      `courses/${courseId}/groups/${groupId}/forum/threads/${threadId}/posts/${postId}/reactions/?emoji=${encodeURIComponent(
        emoji,
      )}`,
    );
  }
}
